import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AvatarUploader } from 'components/ui';
import { FormInput } from '../../Onboarding/components/shared/FormInput';

interface ProfileData {
  firstName: string;
  lastName: string;
  companyName: string;
}

interface ProfileSectionProps {
  initialData?: ProfileData;
  currentAvatarUrl?: string;
  onSave?: (data: ProfileData, avatarFile?: File, removeAvatar?: boolean) => void;
  onChangePassword?: () => void;
  isSaving?: boolean;
  className?: string;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
  initialData = { firstName: '', lastName: '', companyName: '' },
  currentAvatarUrl,
  onSave,
  onChangePassword,
  isSaving = false,
  className
}) => {
  const [avatarFile, setAvatarFile] = useState<File | undefined>();
  const [removeAvatar, setRemoveAvatar] = useState<boolean>(false);
  const { control, handleSubmit } = useForm<ProfileData>({
    defaultValues: initialData
  });

  const handleAvatarUpload = async (file: File) => {
    setAvatarFile(file);
    setRemoveAvatar(false); // Reset removal flag when uploading new image
  };

  const handleRemoveAvatar = () => {
    setRemoveAvatar(true);
    setAvatarFile(undefined); // Clear any uploaded file
  };

  const onSubmit = (data: ProfileData) => {
    onSave?.(data, avatarFile, removeAvatar);
  };

  return (
    <div className={`max-w-3xl mx-auto p-8 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        Personal Information
      </h2>

      <div className="grid grid-cols-[200px_1fr] gap-8 items-start">
        {/* Left: Profile Image */}
        <div className="flex flex-col items-center gap-4">
          <AvatarUploader
            currentAvatarUrl={currentAvatarUrl}
            onUploadComplete={handleAvatarUpload}
            onRemoveImage={handleRemoveAvatar}
            removed={removeAvatar}
            size="lg"
            userName={initialData?.firstName}
          />
        </div>

        {/* Right: Form Fields */}
        <div className="space-y-4">
          {/* First Name */}
          <FormInput
            name="firstName"
            control={control}
            label="First Name"
            placeholder="Jane"
          />

          {/* Last Name */}
          <FormInput
            name="lastName"
            control={control}
            label="Last Name"
            placeholder="Doe"
          />

          {/* Company Name */}
          <FormInput
            name="companyName"
            control={control}
            label="Company Name"
            placeholder="Company Name"
          />

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving}
              className="bg-primary text-white rounded-md px-4 py-2 font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <div className="text-gray-400">|</div>
            <button
              type="button"
              onClick={onChangePassword}
              className="bg-blue-600 text-white rounded-md px-4 py-2 font-medium hover:bg-blue-700 transition"
            >
              Reset Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;